import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { MenuItems } from 'src/app/AppRoutes';
import { useAppContext } from 'src/contexts/AppContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSwitchAccount } from 'src/contexts/SwitchAccountContext';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useHasReleaseNotesUnread } from 'src/hooks/useHasReleaseNotesUnread';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import {
  DropdownDivider,
  DropdownLabel,
} from 'src/navigations/MainNav/MainNavTopBar/MainNavTopBar.styled';
import { UserInfoDisplay } from 'src/navigations/MainNav/UserInfoDisplay';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { UnreadDot } from 'src/navigations/Routes/ReleaseNotes';
import {
  IconsFill,
  ProfileOutlineIcon,
  SigninIcon,
  SignoutIcon,
} from 'src/svgs/Viagogo';
import { REACT_APP_REPORTS_BRIDGE_URL } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { Permission } from 'src/WebApiController';

import {
  NavNotificationsIcon,
  NavPaymentsIcon,
  NavProfileIcon,
  NavSignoutIcon,
  NavSupportIcon,
  NavSwitchIcon,
  NavWhatsNewIcon,
} from '../icons';
import { MenuItem } from '../MenuItem/MenuItem';
import {
  icon,
  iconLegacy,
  menuItem,
  menuItemLegacy,
  menuText,
  menuTextLegacy,
  releaseNoteOptionContainer,
} from '../MenuItem/MenuItem.css';
import { accountRow } from './UserMenu.css';
const menuItems = [MainRoute.Settings] as const;

export const UserMenu = ({
  toggleClose,
  collapsedNav,
}: {
  toggleClose: () => void;
  collapsedNav: boolean;
}) => {
  const { mainRoute } = useAppContext();
  return (
    <div>
      {collapsedNav ? (
        <Dropdown toggleClose={toggleClose} />
      ) : (
        <LegacyDropdown toggleClose={toggleClose} />
      )}
      {menuItems.map((item, index) => {
        const { view, path, label, icon, collapsedNavIcon } = MenuItems[item];
        return (
          <MenuItem
            key={index}
            isActive={mainRoute === view}
            textContent={label}
            icon={collapsedNav ? (collapsedNavIcon ?? icon) : icon}
            path={path}
            onClick={toggleClose}
            collapsedNav={collapsedNav}
          />
        );
      })}
    </div>
  );
};

const MyAccountMenuItem = ({ collapsedNav }: { collapsedNav: boolean }) => {
  const { loginContext } = useAppContext();
  const userDisplayName = loginContext?.user?.activeAccount.accountName;
  return (
    <div className={clsx(menuItem, { [menuItemLegacy]: !collapsedNav })}>
      <div className={clsx(icon, { [iconLegacy]: !collapsedNav })}>
        {collapsedNav ? (
          <NavProfileIcon
            size="24px"
            withHoverEffect
            fill={IconsFill.currentColor}
          />
        ) : (
          <ProfileOutlineIcon />
        )}
      </div>
      <span className={clsx(menuText, { [menuTextLegacy]: !collapsedNav })}>
        {userDisplayName ?? <Content id={ContentId.MyAccount} />}
      </span>
    </div>
  );
};

const Dropdown = ({ toggleClose }: { toggleClose: () => void }) => {
  const { loginContext } = useAppContext();
  const navigate = useNavigate();
  const { onSwitchAccountClicked } = useSwitchAccount();
  const switchAccountText = useContent(ContentId.SwitchAccount);
  const canSwitchAccount = !!loginContext?.user?.hasManyAccounts;
  const handleAccountRowClicked = () => {
    onSwitchAccountClicked();
    toggleClose();
  };

  const { hasReleaseNotesUnread } = useHasReleaseNotesUnread({});
  const canViewReleaseNotes = useUserHasAnyOfPermissions(
    Permission.General_ViewReleaseNotes
  );
  const canViewHelpCenter = useUserHasAnyOfPermissions(
    Permission.General_ViewHelpCenter
  );
  const canViewNotifications = useUserHasAnyOfPermissions(
    Permission.General_ViewNotifications
  );
  const canViewStubhubPayments = useUserHasAnyOfPermissions(
    Permission.Accounting_ViewStubhubPayments
  );

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noreferrer');
  };

  return (
    <>
      {loginContext ? (
        <PosDropdown
          trigger={<MyAccountMenuItem collapsedNav />}
          align="start"
          triggerProps={{ style: { width: '100%' } }}
          contentProps={{ style: { width: '260px', minWidth: '260px' } }}
        >
          <>
            <div
              className={accountRow}
              onClick={canSwitchAccount ? handleAccountRowClicked : undefined}
              role={canSwitchAccount ? 'button' : undefined}
              tabIndex={canSwitchAccount ? 0 : undefined}
              onKeyDown={
                canSwitchAccount
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleAccountRowClicked();
                      }
                    }
                  : undefined
              }
            >
              <div style={{ display: 'flex', flexShrink: 0 }}>
                <NavProfileIcon size="24px" fill={IconsFill.currentColor} />
              </div>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '14px',
                }}
              >
                {loginContext.user?.activeAccount?.accountName}
              </span>
              {loginContext.user?.hasManyAccounts && (
                <PosDropdownItem
                  aria-label={switchAccountText}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleAccountRowClicked();
                  }}
                  style={{
                    marginLeft: 'auto',
                    width: 'auto',
                    flexShrink: 0,
                    padding: vars.spacing.xs,
                    backgroundColor: vars.color.surfaceSubtle,
                    borderRadius: vars.borderRadius.s,
                    cursor: 'pointer',
                  }}
                >
                  <NavSwitchIcon fill={IconsFill.currentColor} size="20px" />
                </PosDropdownItem>
              )}
            </div>
            {canViewStubhubPayments && (
              <PosDropdownItem
                fontWeight={400}
                onClick={() => {
                  openInNewTab(REACT_APP_REPORTS_BRIDGE_URL!);
                  toggleClose();
                }}
              >
                <Stack gap="m" alignItems="center">
                  <NavPaymentsIcon size="24px" fill={IconsFill.currentColor} />
                  <Content id={ContentId.Payments} />
                </Stack>
              </PosDropdownItem>
            )}

            {canViewReleaseNotes && (
              <PosDropdownItem
                fontWeight={400}
                onClick={() => {
                  navigate('/release-notes');
                  toggleClose();
                }}
              >
                <Stack gap="m" alignItems="center">
                  <NavWhatsNewIcon size="24px" fill={IconsFill.currentColor} />
                  <div className={releaseNoteOptionContainer}>
                    <Content id={ContentId.WhatsNew} />
                    {hasReleaseNotesUnread ? <UnreadDot /> : null}
                  </div>
                </Stack>
              </PosDropdownItem>
            )}

            {canViewHelpCenter && (
              <PosDropdownItem
                fontWeight={400}
                onClick={() => {
                  navigate('/help');
                  toggleClose();
                }}
              >
                <Stack gap="m" alignItems="center">
                  <NavSupportIcon size="24px" fill={IconsFill.currentColor} />
                  <Content id={ContentId.HelpCenter} />
                </Stack>
              </PosDropdownItem>
            )}

            {canViewNotifications && (
              <PosDropdownItem
                fontWeight={400}
                onClick={() => {
                  navigate('/notifications');
                  toggleClose();
                }}
              >
                <Stack gap="m" alignItems="center">
                  <NavNotificationsIcon
                    size="24px"
                    fill={IconsFill.currentColor}
                  />
                  <Content id={ContentId.Notifications} />
                </Stack>
              </PosDropdownItem>
            )}

            <PosDropdownItem
              fontWeight={400}
              onClick={() => {
                navigate('/logout');
                toggleClose();
              }}
              style={{ marginTop: vars.spacing.m }}
            >
              <Stack gap="m" alignItems="center">
                <NavSignoutIcon fill={IconsFill.currentColor} size="24px" />
                <Content id={ContentId.SignOut} />
              </Stack>
            </PosDropdownItem>
          </>
        </PosDropdown>
      ) : (
        <MenuItem
          textContent={<Content id={ContentId.SignIn} />}
          path={'/login'}
          icon={SigninIcon}
          onClick={toggleClose}
        />
      )}
    </>
  );
};

// POS-8591: pre-collapsed-nav dropdown, rendered when feature 775 is disabled
const LegacyDropdown = ({ toggleClose }: { toggleClose: () => void }) => {
  const { loginContext } = useAppContext();
  const signInText = useContent(ContentId.SignIn);
  const navigate = useNavigate();
  const { onSwitchAccountClicked } = useSwitchAccount();

  const myAccountText = useContent(ContentId.MyAccount);
  const menuText = loginContext ? myAccountText : signInText;

  const { hasReleaseNotesUnread } = useHasReleaseNotesUnread({});
  const canViewReleaseNotes = useUserHasAnyOfPermissions(
    Permission.General_ViewReleaseNotes
  );
  const canViewHelpCenter = useUserHasAnyOfPermissions(
    Permission.General_ViewHelpCenter
  );
  const canViewStubhubPayments = useUserHasAnyOfPermissions(
    Permission.Accounting_ViewStubhubPayments
  );

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noreferrer');
  };

  return (
    <>
      {loginContext ? (
        <PosDropdown
          trigger={<MyAccountMenuItem collapsedNav={false} />}
          align="start"
          triggerProps={{ style: { width: '100%' } }}
        >
          <>
            <PosDropdownItem disabled style={{ color: vars.color.textPrimary }}>
              {loginContext ? (
                <UserInfoDisplay
                  userInfo={loginContext.user}
                  showUserName={false}
                />
              ) : (
                <DropdownLabel>{menuText}</DropdownLabel>
              )}
            </PosDropdownItem>
            <DropdownDivider />
            {loginContext?.user?.hasManyAccounts && (
              <PosDropdownItem
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onSwitchAccountClicked();
                  toggleClose();
                }}
              >
                <Content id={ContentId.SwitchAccount} />
              </PosDropdownItem>
            )}
            {canViewStubhubPayments && (
              <PosDropdownItem
                onClick={() => {
                  openInNewTab(REACT_APP_REPORTS_BRIDGE_URL!);
                  toggleClose();
                }}
              >
                <Content id={ContentId.Payments} />
              </PosDropdownItem>
            )}

            {canViewReleaseNotes && (
              <PosDropdownItem
                onClick={() => {
                  navigate('/release-notes');
                  toggleClose();
                }}
              >
                <div className={releaseNoteOptionContainer}>
                  <Content id={ContentId.WhatsNew} />
                  {hasReleaseNotesUnread ? <UnreadDot /> : null}
                </div>
              </PosDropdownItem>
            )}

            {canViewHelpCenter && (
              <PosDropdownItem
                onClick={() => {
                  navigate('/help');
                  toggleClose();
                }}
              >
                <div className={releaseNoteOptionContainer}>
                  <Content id={ContentId.Help} />
                </div>
              </PosDropdownItem>
            )}

            <DropdownDivider />
            <PosDropdownItem
              onClick={() => {
                navigate('/logout');
                toggleClose();
              }}
            >
              <Stack gap="m" alignItems="center">
                <SignoutIcon size={vars.iconSize.l} />
                <Content id={ContentId.SignOut} />
              </Stack>
            </PosDropdownItem>
          </>
        </PosDropdown>
      ) : (
        <MenuItem
          textContent={<Content id={ContentId.SignIn} />}
          path={'/login'}
          icon={SigninIcon}
          onClick={toggleClose}
        />
      )}
    </>
  );
};
